import { Component, OnDestroy, OnInit, inject, input, output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { BehaviorSubject, filter, map, Observable, Subscription, take, tap } from 'rxjs';
import { logoutUser } from 'src/app/auth/state/actions/auth.actions';
import { selectUserId, selectUserRoles } from 'src/app/auth/state/selectors/auth.selectors';
import { selectSelectedTenant } from 'src/app/auth/state/selectors/user-tenant.selector';
import { ConfirmActionComponent } from 'src/app/shared/components/confirm-action/confirm-action.component';
import { User } from '../../models/users';
import { selectViewportState } from '../../store/layout/reducer';
import { RouterLinkActive, RouterLink } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { LanguageTogglerComponent } from './language-toggler.component';
import { MatIconButton } from '@angular/material/button';
import { AsyncPipe, UpperCasePipe } from '@angular/common';
import { UppercaseFirstCharPipe } from '../../../shared/pipes/uppercase-first-char.pipe';
import { TranslateModule } from '@ngx-translate/core';

interface RouteWithPermission {
  name: string;
  icon?: string,
  path: string[];
  visibleTo: User.UserRole[];
  order?: number;
  label?: string;
}

@Component({
    selector: 'app-nav',
    styleUrls: ['./nav.component.scss'],
    template: `
    <header class="pt-0" [class.flex-col]="align() === 'vertical'">
        @if (isMobileView$ | async; as view) {
          <nav [class.bg-dark]="view.isMobile" class="d-flex flex-md-row h-full justify-content-end align-items-center">
          @for (route of menuRoutes$ | async; track route) {
            @if (userRoles$ | async; as roles) {
              @if (canViewRoute(route, roles)) {
                <a
                  [routerLink]="route.path"
                  routerLinkActive="active-link"
                  [routerLinkActiveOptions]="{exact:false}"
                  class="no-underline ms-3 mx-md-3 nav-link-light text-size-default"
                  [style.order]="route.order || 'auto'"
                  (click)="closeNav.emit()"
                  >
                  @if (route.label) {
                    {{ route.label | uppercaseFirstChar }}
                  } @else {
                    @if (route.icon) {
                      <mat-icon>{{route.icon}}</mat-icon>
                    } @else {
                      {{ 'NAV.' + route.name | uppercase | translate }}
                    }
                  }
                </a>
              }
            }
          }

        <app-language-toggler class="" [hidden]="false" style="order:99;"></app-language-toggler>

        <!-- logout -->
        @if (loggedIn$ | async; as loggedIn) {
          <button
            id="logout-btn"
            mat-icon-button
            (click)="logout()"
            style="order:100"
            class="nav-link-light"
            [class.mt-2]="view.isMobile"
            >
            <mat-icon [class.text-size-default]="!view.isMobile" style="font-size: 18px"> logout </mat-icon>
          </button>
        }
      </nav>
      }
    </header>
    `,
    imports: [RouterLinkActive, RouterLink, MatIcon, LanguageTogglerComponent, MatIconButton, AsyncPipe, UpperCasePipe, UppercaseFirstCharPipe, TranslateModule]
})
export class NavComponent implements OnInit, OnDestroy {
  private store = inject(Store);
  private dialog = inject(MatDialog);

  readonly align = input<'vertical' | 'horizontal'>('horizontal');

  readonly closeNav = output();

  isMobileView$!: Observable<{ isMobile: boolean }>;

  userRoles$: Observable<User.UserRole[]> = this.store.select(selectUserRoles);

  readonly initialRoutes: RouteWithPermission[] = [
    {
      name: 'DASHBOARD',
      path: ['/home'],
      visibleTo: [User.UserRole.USER, User.UserRole.MANAGER],
      order: 0,
    },
    {
      name: 'TENANTS',
      path: ['mgmt', 'tenants'],
      visibleTo: [User.UserRole.NATIVA],
    },
    {
      name: 'USERS',
      path: ['mgmt', 'users'],
      visibleTo: [User.UserRole.NATIVA],
    }
  ];

  menuRoutes$ = new BehaviorSubject<RouteWithPermission[]>(this.initialRoutes);

  // eslint-disable-next-line @ngrx/avoid-mapping-selectors
  loggedIn$ = this.store.select(selectUserId).pipe(map(Boolean));

  sub!: Subscription;

  ngOnInit(): void {
    // eslint-disable-next-line @ngrx/avoid-mapping-selectors
    this.isMobileView$ = this.store.select(selectViewportState).pipe(map((isMobile: boolean) => ({ isMobile })));

    // This subscription should be open, if user changes his tenant the planning link will be updated
    this.store
      .select(selectSelectedTenant)
      .pipe(
        filter((tenant) => !!tenant),
        map(({ _id }) => _id),
        tap((id) => {
          this.menuRoutes$.next([
            ...this.initialRoutes,
            {
                name: 'PLANNING',
                path: [id, 'planning'],
                visibleTo: [User.UserRole.USER],
                order: 2,
            },
            {
              name: 'REPORT',
              path: [id, 'reports'],
              visibleTo: [User.UserRole.USER],
              order: 3,
            },
            {
              name: 'MANAGE_ACCOUNT',
              path: [id, 'account'],
              visibleTo: [User.UserRole.USER, User.UserRole.MANAGER],
              order: 4,
            },
          ]);
        })
      )
      .subscribe();
  }

  canViewRoute(route: RouteWithPermission, roles: User.UserRole[]): boolean {
    return roles.some((role) => route.visibleTo.includes(role));
  }

  logout(): void {
    const dialogRef = this.dialog.open(ConfirmActionComponent);
    this.sub = dialogRef
      .afterClosed()
      .pipe(
        filter((resp) => !!resp),
        tap(() => {
          this.store.dispatch(logoutUser());
          this.closeNav.emit();
          this.menuRoutes$.next(this.initialRoutes);
        })
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
  }
}